/**
 * Three.js 3D 知识图谱渲染组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import type { GraphNode, GraphEdge } from '../../store/graphStore'

interface GraphCanvas3DProps {
  nodes: GraphNode[]
  edges: GraphEdge[]
  onNodeHover?: (node: GraphNode | null, position: { x: number; y: number }) => void
  onNodeClick?: (node: GraphNode) => void
  className?: string
}

// 颜色配置
const TYPE_COLORS: Record<string, number> = {
  User: 0xAF52DE,
  Person: 0x007AFF,
  Name: 0x007AFF,
  Family: 0xFF2D55,
  Friend: 0x34C759,
  City: 0x5AC8FA,
  Hobby: 0xAF52DE,
  Occupation: 0xFF9500,
  Company: 0xFF9500,
  Food: 0xFF9500,
  FavoriteFood: 0xFF9500,
  Pet: 0xFFCC00,
  Event: 0x5856D6,
  Other: 0x8E8E93,
}

const getTypeColor = (type: string): number => TYPE_COLORS[type] || TYPE_COLORS.Other

// 根据置信度计算透明度（调低整体透明度，更通透）
function getOpacity(confidence: number): number {
  if (confidence >= 0.8) return 0.65  // 高置信度：65%
  if (confidence >= 0.5) return 0.5   // 中置信度：50%
  return 0.35                          // 低置信度：35%
}

export default function GraphCanvas3D({
  nodes,
  edges,
  onNodeHover,
  onNodeClick,
  className = '',
}: GraphCanvas3DProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return true
  })
  
  // Three.js 对象
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const labelRendererRef = useRef<CSS2DRenderer | null>(null)
  const controlsRef = useRef<OrbitControls | null>(null)
  
  // 数据存储
  const meshesRef = useRef<THREE.Mesh[]>([])
  const linesRef = useRef<THREE.Mesh[]>([])
  const labelsRef = useRef<CSS2DObject[]>([])
  const nodeMapRef = useRef<Map<string, { mesh: THREE.Mesh; node: GraphNode }>>(new Map())
  
  // 力导向模拟（Apple Game Center 风格漂浮）
  const velocitiesRef = useRef<Map<string, THREE.Vector3>>(new Map())
  const fixedPosRef = useRef<Map<string, THREE.Vector3 | null>>(new Map()) // fx/fy 固定位置
  const alphaRef = useRef(1)
  const alphaTargetRef = useRef(0.02)  // 保持微小的持续运动
  const alphaDecayRef = useRef(0.015)  // 更慢的衰减
  const velocityDecayRef = useRef(0.92)  // 更高的保持
  const timeRef = useRef(0)  // 用于正弦波漂浮
  const runSimulationRef = useRef<(() => void) | null>(null)
  
  // 交互状态
  const [isDragging, setIsDragging] = useState(false)
  const draggedMeshRef = useRef<THREE.Mesh | null>(null)
  const raycaster = useRef(new THREE.Raycaster())
  const mouse = useRef(new THREE.Vector2())
  const dragPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0))
  
  // 初始化标记
  const initializedRef = useRef(false)
  const [sceneReady, setSceneReady] = useState(false)
  
  // 数据版本号（用于检测数据是否真正变化）
  const dataVersionRef = useRef<string>('')

  // 初始化场景（只执行一次）
  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return
    initializedRef.current = true
    
    const container = containerRef.current
    const width = container.clientWidth || 800
    const height = container.clientHeight || 600
    
    // 场景
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(isDark ? 0x1C1C1E : 0xF5F5F7)
    sceneRef.current = scene
    
    // 相机
    const camera = new THREE.PerspectiveCamera(60, width / height, 1, 2000)
    camera.position.set(0, 0, 350)
    cameraRef.current = camera
    
    // 渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    container.appendChild(renderer.domElement)
    rendererRef.current = renderer
    
    // 标签渲染器
    const labelRenderer = new CSS2DRenderer()
    labelRenderer.setSize(width, height)
    labelRenderer.domElement.style.position = 'absolute'
    labelRenderer.domElement.style.top = '0'
    labelRenderer.domElement.style.left = '0'
    labelRenderer.domElement.style.pointerEvents = 'none'
    container.appendChild(labelRenderer.domElement)
    labelRendererRef.current = labelRenderer
    
    // 轨道控制
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.minDistance = 100
    controls.maxDistance = 800
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    }
    controlsRef.current = controls
    
    // 光照（Apple Game Center 风格 - 多光源营造立体感）
    // 环境光（柔和基础照明）
    scene.add(new THREE.AmbientLight(0xffffff, 0.6))
    
    // 主光源（从左上方，产生高光）
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.9)
    mainLight.position.set(-150, 200, 300)
    scene.add(mainLight)
    
    // 补光（从右下方，减少阴影）
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4)
    fillLight.position.set(150, -100, 200)
    scene.add(fillLight)
    
    // 背光（增加轮廓感）
    const backLight = new THREE.DirectionalLight(0xffffff, 0.3)
    backLight.position.set(0, 0, -300)
    scene.add(backLight)
    
    // 渲染循环（包含力导向模拟）
    let animationId: number
    const animate = () => {
      animationId = requestAnimationFrame(animate)
      
      // 运行力导向模拟
      runSimulationRef.current?.()
      
      controls.update()
      renderer.render(scene, camera)
      labelRenderer.render(scene, camera)
    }
    animate()
    
    // 标记场景准备就绪
    setSceneReady(true)
    
    // 窗口大小变化
    const handleResize = () => {
      const w = container.clientWidth || 800
      const h = container.clientHeight || 600
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
      labelRenderer.setSize(w, h)
    }
    window.addEventListener('resize', handleResize)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      initializedRef.current = false
      setSceneReady(false)
      cancelAnimationFrame(animationId)
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement)
      }
      if (container.contains(labelRenderer.domElement)) {
        container.removeChild(labelRenderer.domElement)
      }
      renderer.dispose()
    }
  }, [isDark])
  
  // 更新背景色
  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.background = new THREE.Color(isDark ? 0x1C1C1E : 0xF5F5F7)
    }
  }, [isDark])

  // 计算数据版本号
  const dataVersion = useMemo(() => {
    const nodeIds = nodes.map(n => n.id).sort().join(',')
    const edgeIds = edges.map(e => e.id).sort().join(',')
    return `${nodeIds}|${edgeIds}`
  }, [nodes, edges])
  
  // 创建图谱内容
  useEffect(() => {
    const scene = sceneRef.current
    
    // 等待场景准备就绪
    if (!scene || !sceneReady || nodes.length === 0) {
      console.log('[GraphCanvas3D] 等待场景就绪或数据', { sceneReady, nodeCount: nodes.length })
      return
    }
    
    // 检查数据是否真正变化
    if (dataVersionRef.current === dataVersion) {
      console.log('[GraphCanvas3D] 数据未变化，跳过重建')
      return // 数据没变，不重建
    }
    dataVersionRef.current = dataVersion
    
    console.log('[GraphCanvas3D] 数据变化，重建图谱', JSON.stringify({ 
      nodeCount: nodes.length, 
      edgeCount: edges.length,
      nodes: nodes.map(n => ({ id: n.id, name: n.name, type: n.type })),
      edges: edges.map(e => ({ 
        id: e.id, 
        sourceId: e.sourceId, 
        targetId: e.targetId,
        sourceName: e.sourceName,
        targetName: e.targetName,
        relation: e.relation 
      })),
    }, null, 2))
    
    // 清除旧内容
    meshesRef.current.forEach(m => {
      scene.remove(m)
      m.geometry.dispose()
      ;(m.material as THREE.Material).dispose()
    })
    linesRef.current.forEach(l => {
      scene.remove(l)
      l.geometry.dispose()
      ;(l.material as THREE.Material).dispose()
    })
    labelsRef.current.forEach(l => scene.remove(l))
    
    meshesRef.current = []
    linesRef.current = []
    labelsRef.current = []
    nodeMapRef.current.clear()
    
    // 创建节点
    const totalNodes = nodes.length
    const positions = new Map<string, THREE.Vector3>()
    
    nodes.forEach((node, index) => {
      const confidence = node.confidence ?? 0.8
      const opacity = getOpacity(confidence)
      const color = getTypeColor(node.type)
      
      // 节点大小
      const baseRadius = 22
      const bonus = Math.min(Math.log2((node.relationCount || 0) + 1) * 7, 20)
      const radius = baseRadius + bonus
      
      // 创建球体（Apple Game Center 风格材质）
      const geometry = new THREE.SphereGeometry(radius, 48, 48)  // 更高细分
      
      // 计算更亮的高光颜色
      const colorObj = new THREE.Color(color)
      const hsl = { h: 0, s: 0, l: 0 }
      colorObj.getHSL(hsl)
      const specularColor = new THREE.Color().setHSL(hsl.h, hsl.s * 0.5, Math.min(1, hsl.l + 0.4))
      
      const material = new THREE.MeshPhongMaterial({
        color,
        transparent: true,
        opacity: Math.min(0.92, opacity + 0.15),  // 稍微不透明一些
        emissive: color,
        emissiveIntensity: 0.15,  // 柔和的内发光
        specular: specularColor,  // 高光颜色
        shininess: 100,  // 更强的光泽
        reflectivity: 0.8,
      })
      
      const mesh = new THREE.Mesh(geometry, material)
      
      // 环形布局
      const angle = (index / totalNodes) * Math.PI * 2
      const layoutRadius = Math.max(100, totalNodes * 28)
      mesh.position.set(
        layoutRadius * Math.cos(angle),
        layoutRadius * Math.sin(angle),
        0
      )
      
      positions.set(node.name, mesh.position.clone())
      mesh.userData = { nodeId: node.id, nodeName: node.name }
      
      scene.add(mesh)
      meshesRef.current.push(mesh)
      nodeMapRef.current.set(node.id, { mesh, node })
      
      // 创建标签（在球体中心）- 智能截断
      const getDisplayName = (name: string, radius: number) => {
        // 根据节点大小调整字符限制
        const maxChars = radius > 25 ? 8 : radius > 20 ? 6 : radius > 15 ? 5 : 4
        
        // 中文字符占用更多空间，需要更保守的截断
        const isChinese = /[\u4e00-\u9fa5]/.test(name)
        const actualLimit = isChinese ? Math.max(2, Math.floor(maxChars * 0.7)) : maxChars
        
        return name.length > actualLimit ? name.slice(0, actualLimit) + '…' : name
      }
      
      const labelDiv = document.createElement('div')
      const fontSize = Math.max(12, Math.min(18, radius * 0.5))  // 提升最小字体到12px，最大18px
      labelDiv.style.cssText = `
        color: #fff;
        font-size: ${fontSize}px;
        font-weight: 700;
        text-shadow: 0 1px 3px rgba(0,0,0,0.9);
        pointer-events: none;
      `
      labelDiv.textContent = getDisplayName(node.name, radius)
      
      const label = new CSS2DObject(labelDiv)
      label.position.set(0, 0, 0)
      mesh.add(label)
      labelsRef.current.push(label)
    })
    
    // 创建边
    console.log('[GraphCanvas3D] positions Map keys:', Array.from(positions.keys()))
    let createdEdges = 0
    let skippedEdges = 0
    edges.forEach((edge, idx) => {
      const sourcePos = positions.get(edge.sourceName)
      const targetPos = positions.get(edge.targetName)
      
      if (idx < 5) {
        console.log(`[GraphCanvas3D] 边 ${idx}:`, JSON.stringify({
          sourceName: edge.sourceName,
          targetName: edge.targetName,
          sourceId: edge.sourceId,
          targetId: edge.targetId,
          foundSourcePos: !!sourcePos,
          foundTargetPos: !!targetPos,
        }))
      }
      
      if (!sourcePos || !targetPos) {
        skippedEdges++
        return
      }
      createdEdges++
      
      // 曲线
      const mid = sourcePos.clone().add(targetPos).multiplyScalar(0.5)
      const perp = new THREE.Vector3()
        .crossVectors(targetPos.clone().sub(sourcePos), new THREE.Vector3(0, 0, 1))
        .normalize()
        .multiplyScalar(25)
      mid.add(perp)
      
      const edgeOpacity = (edge.confidence ?? 0.8) >= 0.8 ? 0.9 : 0.6
      
      // 使用管道几何体来实现线条（更细更优雅）
      const tubeRadius = 0.5 // 更细的线条
      const tubePath = new THREE.CatmullRomCurve3([sourcePos.clone(), mid, targetPos.clone()])
      const tubeGeometry = new THREE.TubeGeometry(tubePath, 16, tubeRadius, 6, false)
      const tubeMaterial = new THREE.MeshBasicMaterial({
        color: isDark ? 0xaaaaaa : 0x666666,
        transparent: true,
        opacity: Math.min(0.5, edgeOpacity),  // 更透明
      })
      
      const tube = new THREE.Mesh(tubeGeometry, tubeMaterial)
      tube.userData = { sourceName: edge.sourceName, targetName: edge.targetName, isTube: true }
      scene.add(tube)
      linesRef.current.push(tube)
      
      // 边标签
      const edgeLabelDiv = document.createElement('div')
      // 关系名称智能截断和字体大小
      const getRelationDisplayName = (relation: string) => {
        const maxLength = 12  // 关系名称最大长度
        return relation.length > maxLength ? relation.slice(0, maxLength) + '…' : relation
      }
      
      const distance = positions.get(edge.sourceId) && positions.get(edge.targetId) 
        ? positions.get(edge.sourceId)!.distanceTo(positions.get(edge.targetId)!) : 100
      const relationFontSize = Math.max(10, Math.min(14, Math.sqrt(distance) * 0.8))
      
      edgeLabelDiv.style.cssText = `
        color: ${isDark ? '#000' : '#fff'};
        font-size: ${relationFontSize}px;
        font-weight: 600;
        background: ${isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.85)'};
        padding: 2px 6px;
        border-radius: 4px;
      `
      edgeLabelDiv.textContent = getRelationDisplayName(edge.relation)
      
      const edgeLabel = new CSS2DObject(edgeLabelDiv)
      edgeLabel.position.copy(mid)
      scene.add(edgeLabel)
      labelsRef.current.push(edgeLabel)
    })
    
    console.log('[GraphCanvas3D] 边创建统计:', JSON.stringify({ 
      totalEdges: edges.length, 
      createdEdges, 
      skippedEdges 
    }))
    
    // 初始化速度
    velocitiesRef.current.clear()
    nodes.forEach(node => {
      velocitiesRef.current.set(node.id, new THREE.Vector3(0, 0, 0))
    })
    
    // 重置模拟参数（保持轻柔漂浮）
    alphaRef.current = 1
    alphaTargetRef.current = 0.02  // 持续轻微运动
    
  }, [nodes, edges, isDark, dataVersion, sceneReady])

  // 更新边位置（拖拽和模拟时调用）
  const updateLines = useCallback(() => {
    const scene = sceneRef.current
    if (!scene) return
    
    linesRef.current.forEach((lineOrTube, index) => {
      const { sourceName, targetName, isTube } = lineOrTube.userData as { 
        sourceName: string 
        targetName: string
        isTube?: boolean
      }
      
      let sourcePos: THREE.Vector3 | undefined
      let targetPos: THREE.Vector3 | undefined
      
      meshesRef.current.forEach(mesh => {
        if (mesh.userData.nodeName === sourceName) sourcePos = mesh.position.clone()
        if (mesh.userData.nodeName === targetName) targetPos = mesh.position.clone()
      })
      
      if (!sourcePos || !targetPos) return
      
      const mid = sourcePos.clone().add(targetPos).multiplyScalar(0.5)
      const perp = new THREE.Vector3()
        .crossVectors(targetPos.clone().sub(sourcePos), new THREE.Vector3(0, 0, 1))
        .normalize()
        .multiplyScalar(25)
      mid.add(perp)
      
      if (isTube) {
        // 更新管道几何体
        const tubePath = new THREE.CatmullRomCurve3([sourcePos, mid, targetPos])
        const newGeometry = new THREE.TubeGeometry(tubePath, 20, 1.5, 8, false)
        lineOrTube.geometry.dispose()
        lineOrTube.geometry = newGeometry
      }
      
      // 更新对应的边标签位置
      const edgeLabelIndex = nodes.length + index
      if (labelsRef.current[edgeLabelIndex]) {
        labelsRef.current[edgeLabelIndex].position.copy(mid)
      }
    })
  }, [nodes.length])

  // 力导向模拟（Apple Game Center 风格漂浮）
  const runSimulation = useCallback(() => {
    const meshes = meshesRef.current
    if (meshes.length === 0) return
    
    // 时间递增（用于正弦波漂浮）
    timeRef.current += 0.016  // ~60fps
    
    // Alpha 衰减
    alphaRef.current += (alphaTargetRef.current - alphaRef.current) * alphaDecayRef.current
    
    // 始终进行模拟（包括轻柔漂浮）
    const alpha = Math.max(alphaRef.current, 0.02)  // 最小保持 0.02 的力
    
    if (true) {  // 始终运行
      
      // 斥力（charge force）- strength: -300
      for (let i = 0; i < meshes.length; i++) {
        for (let j = i + 1; j < meshes.length; j++) {
          const mi = meshes[i]
          const mj = meshes[j]
          
          const delta = mj.position.clone().sub(mi.position)
          const dist = delta.length() || 1
          const force = (-300 / (dist * dist)) * alpha
          const direction = delta.normalize()
          
          const vi = velocitiesRef.current.get(mi.userData.nodeId as string)
          const vj = velocitiesRef.current.get(mj.userData.nodeId as string)
          
          if (vi) vi.sub(direction.clone().multiplyScalar(force))
          if (vj) vj.add(direction.clone().multiplyScalar(force))
        }
      }
      
      // 引力（link force）- distance: 120
      linesRef.current.forEach(line => {
        const { sourceName, targetName } = line.userData as { sourceName: string; targetName: string }
        
        const sourceMesh = meshes.find(m => m.userData.nodeName === sourceName)
        const targetMesh = meshes.find(m => m.userData.nodeName === targetName)
        
        if (sourceMesh && targetMesh) {
          const delta = targetMesh.position.clone().sub(sourceMesh.position)
          const dist = delta.length() || 1
          const force = (dist - 120) * 0.03 * alpha
          const direction = delta.normalize()
          
          const vs = velocitiesRef.current.get(sourceMesh.userData.nodeId as string)
          const vt = velocitiesRef.current.get(targetMesh.userData.nodeId as string)
          
          if (vs) vs.add(direction.clone().multiplyScalar(force))
          if (vt) vt.sub(direction.clone().multiplyScalar(force))
        }
      })
      
      // 中心引力
      meshes.forEach(mesh => {
        const v = velocitiesRef.current.get(mesh.userData.nodeId as string)
        if (v) {
          const toCenter = mesh.position.clone().negate().multiplyScalar(0.01 * alpha)
          v.add(toCenter)
        }
      })
      
      // 碰撞检测
      for (let i = 0; i < meshes.length; i++) {
        for (let j = i + 1; j < meshes.length; j++) {
          const mi = meshes[i]
          const mj = meshes[j]
          const ri = (mi.geometry as THREE.SphereGeometry).parameters.radius + 10
          const rj = (mj.geometry as THREE.SphereGeometry).parameters.radius + 10
          
          const delta = mj.position.clone().sub(mi.position)
          const dist = delta.length()
          const minDist = ri + rj
          
          if (dist < minDist && dist > 0) {
            const overlap = (minDist - dist) * 0.5
            const direction = delta.normalize()
            
            const fxi = fixedPosRef.current.get(mi.userData.nodeId as string)
            const fxj = fixedPosRef.current.get(mj.userData.nodeId as string)
            
            if (!fxi) mi.position.sub(direction.clone().multiplyScalar(overlap))
            if (!fxj) mj.position.add(direction.clone().multiplyScalar(overlap))
          }
        }
      }
      
      // 应用速度 + Apple 风格轻柔漂浮
      meshes.forEach((mesh, index) => {
        const nodeId = mesh.userData.nodeId as string
        const v = velocitiesRef.current.get(nodeId)
        const fx = fixedPosRef.current.get(nodeId)
        
        if (fx) {
          // 固定位置（拖拽中）
          mesh.position.copy(fx)
          if (v) v.set(0, 0, 0)
        } else if (v) {
          // 添加轻柔的正弦波漂浮（每个节点有不同的相位）
          const phase = index * 0.7
          const floatX = Math.sin(timeRef.current * 0.5 + phase) * 0.08
          const floatY = Math.cos(timeRef.current * 0.4 + phase * 1.3) * 0.06
          const floatZ = Math.sin(timeRef.current * 0.3 + phase * 0.9) * 0.05
          
          v.x += floatX
          v.y += floatY
          v.z += floatZ
          
          v.multiplyScalar(velocityDecayRef.current)
          mesh.position.add(v)
        }
      })
      
      // 更新边
      updateLines()
    }
  }, [updateLines])
  
  // 更新 ref
  useEffect(() => {
    runSimulationRef.current = runSimulation
  }, [runSimulation])

  // 鼠标按下
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current
    const camera = cameraRef.current
    if (!container || !camera) return
    
    const rect = container.getBoundingClientRect()
    mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.current.setFromCamera(mouse.current, camera)
    const intersects = raycaster.current.intersectObjects(meshesRef.current)
    
    if (intersects.length > 0) {
      const mesh = intersects[0].object as THREE.Mesh
      const nodeId = mesh.userData.nodeId as string
      
      setIsDragging(true)
      draggedMeshRef.current = mesh
      
      // 设置固定位置（类似 D3 的 d.fx = d.x）
      fixedPosRef.current.set(nodeId, mesh.position.clone())
      
      // 拖拽时激活模拟（类似 D3 的 alphaTarget(0.3).restart()）
      alphaRef.current = Math.max(alphaRef.current, 0.3)
      alphaTargetRef.current = 0.3
      
      if (controlsRef.current) {
        controlsRef.current.enabled = false
      }
      
      dragPlane.current.setFromNormalAndCoplanarPoint(
        camera.getWorldDirection(new THREE.Vector3()).negate(),
        mesh.position
      )
    }
  }, [])

  // 鼠标移动
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current
    const camera = cameraRef.current
    if (!container || !camera) return
    
    const rect = container.getBoundingClientRect()
    mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    if (isDragging && draggedMeshRef.current) {
      // 拖拽节点 - 更新固定位置
      raycaster.current.setFromCamera(mouse.current, camera)
      const intersection = new THREE.Vector3()
      raycaster.current.ray.intersectPlane(dragPlane.current, intersection)
      
      if (intersection) {
        const nodeId = draggedMeshRef.current.userData.nodeId as string
        fixedPosRef.current.set(nodeId, intersection.clone())
      }
    } else if (onNodeHover) {
      // Hover 检测
      raycaster.current.setFromCamera(mouse.current, camera)
      const intersects = raycaster.current.intersectObjects(meshesRef.current)
      
      if (intersects.length > 0) {
        const mesh = intersects[0].object as THREE.Mesh
        const nodeId = mesh.userData.nodeId as string
        const data = nodeMapRef.current.get(nodeId)
        if (data) {
          container.style.cursor = 'pointer'
          onNodeHover(data.node, { x: e.clientX - rect.left, y: e.clientY - rect.top })
        }
      } else {
        container.style.cursor = 'grab'
        onNodeHover(null, { x: 0, y: 0 })
      }
    }
  }, [isDragging, onNodeHover])

  // 鼠标松开
  const handleMouseUp = useCallback(() => {
    if (draggedMeshRef.current) {
      const nodeId = draggedMeshRef.current.userData.nodeId as string
      // 释放固定位置（类似 D3 的 d.fx = null）
      fixedPosRef.current.set(nodeId, null)
    }
    
    setIsDragging(false)
    draggedMeshRef.current = null
    
    // 松开后让模拟自然衰减（类似 D3 的 alphaTarget(0)）
    alphaTargetRef.current = 0
    
    if (controlsRef.current) {
      controlsRef.current.enabled = true
    }
  }, [])

  // 点击
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!onNodeClick) return
    
    const container = containerRef.current
    const camera = cameraRef.current
    if (!container || !camera) return
    
    const rect = container.getBoundingClientRect()
    mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.current.setFromCamera(mouse.current, camera)
    const intersects = raycaster.current.intersectObjects(meshesRef.current)
    
    if (intersects.length > 0) {
      const mesh = intersects[0].object as THREE.Mesh
      const nodeId = mesh.userData.nodeId as string
      const data = nodeMapRef.current.get(nodeId)
      if (data) {
        onNodeClick(data.node)
      }
    }
  }, [onNodeClick])

  // 双击重置
  const handleDoubleClick = useCallback(() => {
    const camera = cameraRef.current
    const controls = controlsRef.current
    if (camera && controls) {
      camera.position.set(0, 0, 350)
      controls.target.set(0, 0, 0)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full ${className}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    />
  )
}
